// Re-export everything from client
import type { ClientConfig } from "./client/types";
import { BaseClient } from "./client/base.client";
import { gql } from "urql";
import type { DocumentNode } from "graphql";
import * as sdk from "./generated/sdk";

interface Content {
  core: sdk.ContentsClient;
  news: sdk.NewsClient;
  research: sdk.ResearchClient;
  //   jobs: sdk.JobsClient;
  categories: sdk.ContentCategoriesClient;
  tags: sdk.ContentTagsClient;
  sources: sdk.ContentSourcesClient;
  statuses: sdk.ContentStatusesClient;
  feeds: {
    main: sdk.FeedsClient;
    categories: sdk.FeedCategoriesClient;
    sources: sdk.FeedSourcesClient;
  };
  newsLetters: sdk.NewslettersClient;
  summaries: sdk.NewsSummariesClient;
  embeddings: {
    research: sdk.ResearchEmbeddingsClient;
    reviews: sdk.EmbeddingReviewsClient;
  };
}

interface Companies {
  core: sdk.CompaniesClient;
  employees: sdk.CompanyEmployeesClient;
  contacts: sdk.CompanyContactsClient;
  extras: sdk.CompanyExtrasClient;
  metrics: sdk.CompanyMetricsClient;
}

interface Engagement {
  bookmarks: {
    items: sdk.BookmarksClient;
    folders: sdk.BookmarkFoldersClient;
  };
  comments: sdk.CommentsClient;
  follows: sdk.FollowsClient;
  votes: sdk.VotesClient;
  feedback: sdk.FeedbacksClient;
  referrals: sdk.ReferralsClient;
}

interface Users {
  profiles: sdk.UserProfilesClient;
  metrics: sdk.UserMetricsClient;
  searches: sdk.SearchesClient;
}

interface Location {
  countries: sdk.CountriesClient;
  cities: sdk.CitiesClient;
  addresses: sdk.AddressesClient;
}

interface Security {
  roles: {
    permissions: sdk.RolePermissionsClient;
    hierarchy: sdk.RoleHierarchyClient;
    materialized: sdk.RolePermissionsMaterializedClient;
  };
  planPermissions: sdk.PlanPermissionsClient;
  blockedIps: sdk.BlockedIpsClient;
  blacklists: {
    urls: sdk.BlacklistedUrlsClient;
    domains: sdk.BlacklistedDomainsClient;
  };
  referrerBlocks: sdk.ReferrerBlocksClient;
}

interface Advertising {
  ads: sdk.AdsClient;
  packages: sdk.AdPackagesClient;
  variants: sdk.AdVariantsClient;
  metrics: sdk.AdDailyMetricsClient;
}

interface Billing {
  payments: sdk.CustomerPaymentsClient;
  refunds: sdk.CustomerRefundsClient;
  subscriptions: {
    main: sdk.CustomerSubscriptionsClient;
    plans: sdk.CustomerSubscriptionPlansClient;
  };
  webhooks: sdk.CustomerProcessedWebhooksClient;
  providers: sdk.PaymentProvidersClient;
}

interface System {
  workflows: sdk.WorkflowsClient;
  performance: {
    queries: sdk.TableQueryPerformanceClient;
    maintenance: sdk.TableMaintenanceLogClient;
    sequences: sdk.TableSequenceUsageClient;
    statistics: sdk.TableStatisticsClient;
  };
  features: {
    requests: sdk.FeatureRequestsClient;
    votes: sdk.FeatureVotesClient;
  };
  errors: sdk.ErrorLogsClient;
  circuitBreaker: sdk.CircuitBreakerStatesClient;
  migrations: {
    strapi: sdk.StrapiMigrationsClient;
    internal: sdk.StrapiMigrationsInternalClient;
  };
}

interface Analytics {
  spiderMetrics: sdk.SpiderMetricsClient;
  metricDefinitions: sdk.MetricDefinitionsClient;
  scoringWeights: sdk.ScoringWeightsClient;
  sourceVisits: sdk.ContentSourceVisitsClient;
}

interface Business {
  domains: sdk.BusinessDomainsClient;
  categorizedUrls: sdk.CategorizedUrlsClient;
}

export interface ApiClient {
  content: Content;
  companies: Companies;
  engagement: Engagement;
  users: Users;
  location: Location;
  security: Security;
  advertising: Advertising;
  billing: Billing;
  system: System;
  analytics: Analytics;
  business: Business;
}

export class ApiClient extends BaseClient implements ApiClient {
  public content: Content;
  public companies: Companies;
  public engagement: Engagement;
  public users: Users;
  public location: Location;
  public security: Security;
  public advertising: Advertising;
  public billing: Billing;
  public system: System;
  public analytics: Analytics;
  public business: Business;

  constructor(protected config: ClientConfig) {
    super(config);

    this.content = {
      core: new sdk.ContentsClient(this.config),
      news: new sdk.NewsClient(this.config),
      research: new sdk.ResearchClient(this.config),
      //   jobs: new sdk.JobsClient(this.config),
      categories: new sdk.ContentCategoriesClient(this.config),
      tags: new sdk.ContentTagsClient(this.config),
      sources: new sdk.ContentSourcesClient(this.config),
      statuses: new sdk.ContentStatusesClient(this.config),
      feeds: {
        main: new sdk.FeedsClient(this.config),
        categories: new sdk.FeedCategoriesClient(this.config),
        sources: new sdk.FeedSourcesClient(this.config),
      },
      newsLetters: new sdk.NewslettersClient(this.config),
      summaries: new sdk.NewsSummariesClient(this.config),
      embeddings: {
        research: new sdk.ResearchEmbeddingsClient(this.config),
        reviews: new sdk.EmbeddingReviewsClient(this.config),
      },
    };

    // Company Domain
    this.companies = {
      core: new sdk.CompaniesClient(this.config),
      employees: new sdk.CompanyEmployeesClient(this.config),
      contacts: new sdk.CompanyContactsClient(this.config),
      extras: new sdk.CompanyExtrasClient(this.config),
      metrics: new sdk.CompanyMetricsClient(this.config),
    };

    // User Engagement Domain
    this.engagement = {
      bookmarks: {
        items: new sdk.BookmarksClient(this.config),
        folders: new sdk.BookmarkFoldersClient(this.config),
      },
      comments: new sdk.CommentsClient(this.config),
      follows: new sdk.FollowsClient(this.config),
      votes: new sdk.VotesClient(this.config),
      feedback: new sdk.FeedbacksClient(this.config),
      referrals: new sdk.ReferralsClient(this.config),
    };

    // User Management Domain
    this.users = {
      profiles: new sdk.UserProfilesClient(this.config),
      metrics: new sdk.UserMetricsClient(this.config),
      searches: new sdk.SearchesClient(this.config),
    };

    // Location Domain
    this.location = {
      countries: new sdk.CountriesClient(this.config),
      cities: new sdk.CitiesClient(this.config),
      addresses: new sdk.AddressesClient(this.config),
    };

    // Security & Access Control Domain
    this.security = {
      roles: {
        permissions: new sdk.RolePermissionsClient(this.config),
        hierarchy: new sdk.RoleHierarchyClient(this.config),
        materialized: new sdk.RolePermissionsMaterializedClient(this.config),
      },
      planPermissions: new sdk.PlanPermissionsClient(this.config),
      blockedIps: new sdk.BlockedIpsClient(this.config),
      blacklists: {
        urls: new sdk.BlacklistedUrlsClient(this.config),
        domains: new sdk.BlacklistedDomainsClient(this.config),
      },
      referrerBlocks: new sdk.ReferrerBlocksClient(this.config),
    };

    // Advertising Domain
    this.advertising = {
      ads: new sdk.AdsClient(this.config),
      packages: new sdk.AdPackagesClient(this.config),
      variants: new sdk.AdVariantsClient(this.config),
      metrics: new sdk.AdDailyMetricsClient(this.config),
    };

    // Customer Billing Domain
    this.billing = {
      payments: new sdk.CustomerPaymentsClient(this.config),
      refunds: new sdk.CustomerRefundsClient(this.config),
      subscriptions: {
        main: new sdk.CustomerSubscriptionsClient(this.config),
        plans: new sdk.CustomerSubscriptionPlansClient(this.config),
      },
      webhooks: new sdk.CustomerProcessedWebhooksClient(this.config),
      providers: new sdk.PaymentProvidersClient(this.config),
    };

    // System & Monitoring Domain
    this.system = {
      workflows: new sdk.WorkflowsClient(this.config),
      performance: {
        queries: new sdk.TableQueryPerformanceClient(this.config),
        maintenance: new sdk.TableMaintenanceLogClient(this.config),
        sequences: new sdk.TableSequenceUsageClient(this.config),
        statistics: new sdk.TableStatisticsClient(this.config),
      },
      features: {
        requests: new sdk.FeatureRequestsClient(this.config),
        votes: new sdk.FeatureVotesClient(this.config),
      },
      errors: new sdk.ErrorLogsClient(this.config),
      circuitBreaker: new sdk.CircuitBreakerStatesClient(this.config),
      migrations: {
        strapi: new sdk.StrapiMigrationsClient(this.config),
        internal: new sdk.StrapiMigrationsInternalClient(this.config),
      },
    };

    // Metrics & Analytics Domain
    this.analytics = {
      spiderMetrics: new sdk.SpiderMetricsClient(this.config),
      metricDefinitions: new sdk.MetricDefinitionsClient(this.config),
      scoringWeights: new sdk.ScoringWeightsClient(this.config),
      sourceVisits: new sdk.ContentSourceVisitsClient(this.config),
    };

    // Business Domain
    this.business = {
      domains: new sdk.BusinessDomainsClient(this.config),
      categorizedUrls: new sdk.CategorizedUrlsClient(this.config),
    };
  }
}

export function createClient(config: ClientConfig): ApiClient {
  return new ApiClient(config);
}

export default createClient;
